import { type FormEvent, type JSX, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, ConfirmModal, Input, Skeleton, Spinner, Textarea } from '@/components/ui';
import {
  downloadContactListCSV,
  useContactLists,
  useContactsInList,
  useCreateContactList,
  useDeleteContactList,
  type ContactItem,
  type ContactListItem,
} from '@/hooks/useContactLists';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Create-list modal
// ---------------------------------------------------------------------------
interface CreateListModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (list: ContactListItem) => void;
}

// Common ISO-2 country codes the operator picks from. Not exhaustive on
// purpose — any value still works through the API, this is just a friendly
// dropdown for the most-used markets.
const COUNTRY_OPTIONS = ['US', 'CA', 'CO', 'MX', 'AR', 'BR', 'CL', 'PE', 'ES', 'GB'];

function CreateListModal({ open, onClose, onCreated }: CreateListModalProps): JSX.Element | null {
  const { t } = useTranslation('default');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultCountry, setDefaultCountry] = useState('US');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateContactList();

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setDefaultCountry('US');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('contacts_err_name_required'));
      return;
    }
    try {
      const list = await create.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        default_country: defaultCountry,
      });
      onCreated(list);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate') {
        setError(t('contacts_err_list_duplicate'));
      } else {
        setError(err instanceof Error ? err.message : t('contacts_err_generic'));
      }
    }
  };

  return (
    <ModalShell title={t('contacts_new_list_title')} onClose={onClose}>
      <form onSubmit={submit}>
        {error && (
          <div className="px-3 py-2 mb-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
            {error}
          </div>
        )}
        <label className="block text-[12px] font-medium text-text-2 mb-1.5">
          {t('contacts_field_name')}
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="VIP customers"
          disabled={create.isPending}
        />

        <label className="block text-[12px] font-medium text-text-2 mb-1.5 mt-3">
          {t('contacts_field_default_country')}
        </label>
        <select
          value={defaultCountry}
          onChange={(e) => setDefaultCountry(e.target.value)}
          disabled={create.isPending}
          className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
        >
          {COUNTRY_OPTIONS.map((cc) => (
            <option key={cc} value={cc}>
              {cc}
            </option>
          ))}
        </select>
        <p className="text-[12px] text-text-4 mt-1">
          {t('contacts_field_default_country_help')}
        </p>

        <label className="block text-[12px] font-medium text-text-2 mb-1.5 mt-3">
          {t('contacts_field_description')}
        </label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          disabled={create.isPending}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
            {t('contacts_cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? (
              <>
                <Spinner />
                <span>{t('contacts_creating')}</span>
              </>
            ) : (
              t('contacts_create')
            )}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ImportModal removed - replaced by /contacts/import full-page wizard.

// ---------------------------------------------------------------------------
// Generic modal shell
// ---------------------------------------------------------------------------
interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}

function ModalShell({ title, onClose, children, wide }: ModalShellProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4">
      <div
        className={cn(
          'bg-surface border border-line rounded-lg shadow-lg w-full max-h-[90vh] overflow-auto',
          wide ? 'max-w-[820px]' : 'max-w-[480px]',
        )}
      >
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 grid place-items-center rounded-md text-text-3 hover:bg-surface-3"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function ContactsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const lists = useContactLists();
  const del = useDeleteContactList();

  const navigate = useNavigate();
  const [activeListID, setActiveListID] = useState<number | null>(null);
  const [showNew, setShowNew] = useState(false);

  // Auto-select the first list when the catalog finishes loading and we
  // don't have one selected yet. Without this the right pane is empty
  // until the operator clicks something.
  useEffect(() => {
    if (activeListID === null && lists.data?.items?.[0]) {
      setActiveListID(lists.data.items[0].id);
    }
  }, [activeListID, lists.data]);

  const activeList = useMemo(
    () => lists.data?.items.find((l) => l.id === activeListID) ?? null,
    [lists.data, activeListID],
  );

  const contacts = useContactsInList({ listID: activeListID });

  // Pending delete uses the shared ConfirmModal instead of window.confirm
  // for visual consistency with the rest of the design system.
  const [pendingDeleteID, setPendingDeleteID] = useState<number | null>(null);
  // Export is a plain fetch-to-blob rather than a mutation: there is no
  // cache to invalidate and nothing on the server changes, so a local
  // in-flight flag is the whole state machine.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async (listID: number): Promise<void> => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadContactListCSV(listID);
    } catch {
      setExportError(t('contacts_export_error'));
    } finally {
      setExporting(false);
    }
  };

  const askDelete = (id: number) => setPendingDeleteID(id);

  const confirmDelete = async () => {
    if (pendingDeleteID === null) return;
    const id = pendingDeleteID;
    try {
      await del.mutateAsync(id);
      if (activeListID === id) setActiveListID(null);
      setPendingDeleteID(null);
    } catch {
      // Mutation surfaces error in console; modal stays open so the operator
      // sees something happened.
      setPendingDeleteID(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1280px] mx-auto">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
            {t('nav_contacts')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1">{t('contacts_sub')}</p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <span>{t('contacts_new_list_button')}</span>
        </Button>
      </header>

      <div className="grid grid-cols-12 gap-4">
        {/* Lists column */}
        <Card padding="none" className="col-span-12 lg:col-span-4">
          <div className="px-4 py-3 border-b border-line">
            <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
              {t('contacts_lists_title')}
            </div>
          </div>
          {lists.isLoading ? (
            <div className="p-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : lists.data?.items.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-[14px] text-text-2 font-medium">{t('contacts_no_lists_title')}</p>
              <p className="text-[13px] text-text-3 mt-1">{t('contacts_no_lists_sub')}</p>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {lists.data?.items.map((l) => (
                <li
                  key={l.id}
                  className={cn(
                    'px-4 py-3 cursor-pointer hover:bg-surface-2',
                    activeListID === l.id && 'bg-surface-3',
                  )}
                  onClick={() => setActiveListID(l.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-text truncate">{l.name}</div>
                      {l.description && (
                        <div className="text-[12px] text-text-3 truncate mt-0.5">
                          {l.description}
                        </div>
                      )}
                    </div>
                    <Badge variant="gray" className="num">
                      {l.contact_count.toLocaleString(lang)}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Contacts column */}
        <Card padding="none" className="col-span-12 lg:col-span-8">
          {activeList ? (
            <>
              <div className="px-5 py-4 border-b border-line flex items-center justify-between">
                <div>
                  <h2 className="display text-[16px] font-semibold text-text">{activeList.name}</h2>
                  <p className="text-[12px] text-text-3 mt-0.5">
                    {t('contacts_total', {
                      total: activeList.contact_count.toLocaleString(lang),
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/contacts/import?listId=${activeList.id}`)}
                  >
                    {t('contacts_import_button')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleExport(activeList.id)}
                    disabled={exporting || activeList.contact_count === 0}
                  >
                    {exporting ? (
                      <>
                        <Spinner />
                        <span>{t('contacts_export_running')}</span>
                      </>
                    ) : (
                      t('contacts_export_button')
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => askDelete(activeList.id)}
                    disabled={del.isPending}
                    className="text-danger hover:bg-danger/10"
                  >
                    {t('contacts_delete_list')}
                  </Button>
                </div>
              </div>

              {exportError && (
                <div className="mx-5 mt-4 px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
                  {exportError}
                </div>
              )}

              {contacts.isLoading ? (
                <div className="p-5 space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : contacts.data?.items.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="text-[14px] text-text-2 font-medium">
                    {t('contacts_empty_title')}
                  </p>
                  <p className="text-[13px] text-text-3 mt-1">{t('contacts_empty_sub')}</p>
                </div>
              ) : (
                <ContactsTable
                  items={contacts.data?.items ?? []}
                  lang={lang}
                  t={t as (k: string) => string}
                />
              )}
            </>
          ) : (
            <div className="p-12 text-center">
              <p className="text-[14px] text-text-2 font-medium">
                {t('contacts_select_or_create')}
              </p>
            </div>
          )}
        </Card>
      </div>

      <CreateListModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(l) => setActiveListID(l.id)}
      />
      <ConfirmModal
        open={pendingDeleteID !== null}
        title={t('contacts_delete_list')}
        description={t('contacts_confirm_delete_list')}
        confirmLabel={t('contacts_delete_list')}
        cancelLabel={t('contacts_cancel')}
        tone="danger"
        busy={del.isPending}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDeleteID(null)}
      />
    </div>
  );
}

interface TableProps {
  items: ContactItem[];
  lang: string;
  t: (k: string) => string;
}

function ContactsTable({ items, lang, t }: TableProps): JSX.Element {
  // Collect every distinct custom_fields key across the visible rows so the
  // table renders one column per variable. Order: alphabetical for stability.
  const extraColumns = useMemo(() => {
    const set = new Set<string>();
    for (const c of items) {
      for (const k of Object.keys(c.custom_fields)) set.add(k);
    }
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
            <th className="px-5 py-2.5 font-medium">{t('contacts_col_phone')}</th>
            <th className="px-5 py-2.5 font-medium">{t('contacts_col_country')}</th>
            {extraColumns.map((c) => (
              <th key={c} className="px-5 py-2.5 font-medium">
                {c}
              </th>
            ))}
            <th className="px-5 py-2.5 font-medium whitespace-nowrap">{t('contacts_col_added')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-line last:border-0 hover:bg-surface-2">
              <td className="px-5 py-2.5 num text-text whitespace-nowrap">{c.phone_e164}</td>
              <td className="px-5 py-2.5 num text-text-2 uppercase">{c.country_code ?? '—'}</td>
              {extraColumns.map((col) => (
                <td key={col} className="px-5 py-2.5 text-text-2">
                  {c.custom_fields[col] ?? '—'}
                </td>
              ))}
              <td className="px-5 py-2.5 text-text-3 whitespace-nowrap">
                <span title={new Date(c.created_at).toLocaleString(lang)}>
                  {formatRelativeTime(c.created_at, lang)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
